import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { Input } from '@/common/components/input/Input';
import { Button } from '@/common/components/button/Button';
import FileUpload from '@/common/components/fileUpload/FileUpload';
import { IconButton } from '@/common/components/iconButton/IconButton';
import Avatar from '@/common/components/avatar/Avatar';
import { FileProps } from '@/common/types/file';
import { accountService, UserProfile, CompanyInfo, ChangePasswordDto } from '@/services/accountService';
import { toastUtils } from '@/common/utils/toastUtils';
import { formatPhoneNumber } from '@/common/utils/textFormat';
import { useAuthStore } from '@/store/useAuthStore';
import { useNavigationStore } from '@/common/store';
import { useTutorialStore } from '@/store/useTutorialStore';
import { validatePassword } from '@/common/utils/commonUtil';
import InisisAuthButton, { InisisAuthResult } from '@/components-common/inisis/InisisAuthButton';
import config from '@/config';
import { authService } from '@/services/authService';
import { semanticColors } from '@/common/styles/colorTokens';
import { typography } from '@/common/styles/typographyTokens';
import { ToggleSwitch } from '@/common/components/checkbox';
import TempAuthModal from './components/TempAuthModal';
import EmailChangeModal from './components/EmailChangeModal';
import PasswordChangeModal from './components/PasswordChangeModal';
import ProfileDeleteModal from './components/ProfileDeleteModal';
import AccountSettingsTutorial from '@/components/tutorial/AccountSettingsTutorial';
import {
  User,
  Mail,
  Phone,
  Shield,
  Lock,
  Building2,
  Camera,
  Trash2,
  Edit3,
  Check,
  AlertCircle,
  ChevronRight,
  Settings,
  KeyRound,
  Send,
  RefreshCw,
  UserCircle,
  Briefcase,
  Users,
  GraduationCap,
  PlayCircle,
  Rocket,
  RotateCcw,
  Bell
} from 'lucide-react';

import { 
  HiOutlineHome,
  HiOutlineBuildingOffice2,
  HiOutlineUserCircle
} from 'react-icons/hi2';


import { useNavigate } from 'react-router-dom';
import { TUTORIAL_ROUTES, TutorialPage, TUTORIAL_PAGE_NAMES } from '@/store/useTutorialStore';

const AccountSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setHeaderTitle, setRestartIntro } = useNavigationStore();
  const { startTutorial, loadFromStorage, resetTutorial, resetAllTutorials } = useTutorialStore();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [companyInfos, setCompanyInfos] = useState<CompanyInfo[]>([]);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [tempEmail, setTempEmail] = useState('');
  const [profileFiles, setProfileFiles] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Auth store
  const { user, setUser } = useAuthStore();

  // 비밀번호 변경 관련 상태
  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false);
  const [userCertifyData, setUserCertifyData] = useState<{userCi: string, token: string} | undefined>(undefined);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');

  // 임시 핸드폰 번호 인증 모달 상태
  const [showTempAuthModal, setShowTempAuthModal] = useState(false);
  const [tempPhoneNumber, setTempPhoneNumber] = useState('');
  const [tempPhoneError, setTempPhoneError] = useState('');

  // 이메일 변경 모달 상태
  const [showEmailChangeModal, setShowEmailChangeModal] = useState(false);

  // 이메일 재전송 상태
  const [isResendingEmail, setIsResendingEmail] = useState(false);

  // 프로필 이미지 삭제 확인 모달 상태
  const [showProfileDeleteModal, setShowProfileDeleteModal] = useState(false);
  
  // 튜토리얼 상태
  const [showTutorial, setShowTutorial] = useState(false);

  // 알림톡 수신 설정
  const [alimtalkSmsEnabled, setAlimtalkSmsEnabled] = useState(true);
  const [isUpdatingPreference, setIsUpdatingPreference] = useState(false);

  useEffect(() => {
    fetchAccountSettings();
    fetchMessagePreference();
    setHeaderTitle('설정');
    
    // 인트로 재시작 함수 등록
    setRestartIntro(() => {
      resetTutorial('account-settings');
      setTimeout(() => {
        setShowTutorial(true);
      }, 300);
    });
    
    // 튜토리얼 상태 로드
    loadFromStorage();
    
    // URL 파라미터로 튜토리얼 시작 여부 확인
    const shouldStartTutorial = searchParams.get('tutorial') === 'true';
    if (shouldStartTutorial) {
      startTutorial('account-settings');
      setShowTutorial(true);
      // URL에서 tutorial 파라미터 제거
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('tutorial');
      window.history.replaceState({}, document.title, newUrl.toString());
    }
    
    // cleanup: 페이지 떠날 때 인트로 재시작 함수 제거
    return () => {
      setRestartIntro(null);
    };
  }, [searchParams, loadFromStorage, startTutorial, setHeaderTitle, setRestartIntro, resetTutorial]);
  
  const handleTutorialComplete = () => {
    setShowTutorial(false);
  };
  
  // 특정 페이지 튜토리얼 다시 보기
  const handleRestartTutorial = (page: TutorialPage) => {
    resetTutorial(page);
    navigate(TUTORIAL_ROUTES[page] + '?tutorial=true');
  };
  
  // 전체 튜토리얼 다시 보기
  const handleRestartAllTutorials = () => {
    resetAllTutorials();
    navigate('/my-space?tutorial=true');
  };

  const fetchAccountSettings = async () => {
    try {
      setIsLoading(true);
      const response = await accountService.getAccountSettings();
      setUserProfile(response.userProfile);
      setCompanyInfos(response.companyInfos);
      setTempEmail(response.userProfile.email);
      
      return response;
    } catch (error) {
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMessagePreference = async () => {
    try {
      const response = await accountService.getMessagePreference();
      setAlimtalkSmsEnabled(response.alimtalkSmsEnabled);
    } catch {
      setAlimtalkSmsEnabled(true);
    }
  };

  const handleAlimtalkPreferenceChange = async (checked: boolean) => {
    const previous = alimtalkSmsEnabled;
    setAlimtalkSmsEnabled(checked);
    setIsUpdatingPreference(true);
    try {
      const response = await accountService.updateMessagePreference({
        alimtalkSmsEnabled: checked,
      });
      setAlimtalkSmsEnabled(response.alimtalkSmsEnabled);
      toastUtils.success(
        checked
          ? '알림톡 수신이 허용되었습니다.'
          : '알림톡 수신이 거부되었습니다.'
      );
    } catch {
      setAlimtalkSmsEnabled(previous);
      toastUtils.error('알림 설정 변경에 실패했습니다.');
    } finally {
      setIsUpdatingPreference(false);
    }
  };

  const handleEmailEdit = () => {
    if (userProfile) {
      setShowEmailChangeModal(true);
    }
  };

  const handleEmailSave = async () => {
    await authService.setUserProfile();
    await fetchAccountSettings();
    setShowEmailChangeModal(false);
  };

  const handleEmailCancel = () => {
    setShowEmailChangeModal(false);
  };

  const handleResendVerificationEmail = async () => {
    if (!userProfile?.email) return;

    setIsResendingEmail(true);
    
    try {
      const result = await authService.resendVerifyEmail(userProfile.email);
      if (result.success) {
        toastUtils.success('인증 이메일이 재전송되었습니다.');
      } else {
        toastUtils.error('이메일 재전송에 실패했습니다.');
      }
    } catch (error) {
      console.error('이메일 재전송 실패:', error);
      toastUtils.error('이메일 재전송에 실패했습니다.');
    } finally {
      setIsResendingEmail(false);
    }
  };

  const handleProfileImageUpload = (files: File[]) => {
    setProfileFiles(files);
  };

  const handleProfileImageSave = async () => {
    if (profileFiles.length === 0) return;

    try {
      setIsUpdating(true);
      await accountService.updateAccountSettings({ profileImage: profileFiles[0] });
      
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const updatedProfile = await fetchAccountSettings();
      console.log('계정 설정 새로고침 완료');
      
      if (user && updatedProfile?.userProfile?.profileImage) {
        setUser({
          ...user,
          profileImage: updatedProfile.userProfile.profileImage.url
        });
      }
      
      setProfileFiles([]);
      toastUtils.success('프로필 이미지가 성공적으로 변경되었습니다.');
    } catch (error) {
      console.error('프로필 이미지 업데이트 실패:', error);
      toastUtils.error('프로필 이미지 변경에 실패했습니다.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleProfileImageRemove = async () => {
    try {
      setIsUpdating(true);
      await accountService.deleteProfileImage();
      await fetchAccountSettings();
      await authService.setUserProfile();
      
      setProfileFiles([]);
      setShowProfileDeleteModal(false);
      toastUtils.success('프로필 이미지가 성공적으로 삭제되었습니다.');
    } catch (error) {
      console.error('프로필 이미지 삭제 실패:', error);
      toastUtils.error('프로필 이미지 삭제에 실패했습니다.');
    } finally {
      setIsUpdating(false);
    }
  };

  const validatePhoneNumber = (phone: string): boolean => {
    const phoneRegex = /^010-\d{4}-\d{4}$/;
    return phoneRegex.test(phone);
  };

  const formatTempPhoneNumber = (value: string): string => {
    const numbers = value.replace(/[^0-9]/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
  };

  const handleTempPhoneNumberChange = (value: string) => {
    const formatted = formatTempPhoneNumber(value);
    setTempPhoneNumber(formatted);
    setTempPhoneError('');
  };

  const handleTempAuthModalOpen = () => {
    setShowTempAuthModal(true);
    setTempPhoneNumber('');
    setTempPhoneError('');
  };

  const handleTempAuthModalClose = () => {
    setShowTempAuthModal(false);
    setTempPhoneNumber('');
    setTempPhoneError('');
  };

  const handleTempAuthSubmit = () => {
    if (!tempPhoneNumber) {
      setTempPhoneError('핸드폰번호를 입력해주세요.');
      return;
    }

    if (!validatePhoneNumber(tempPhoneNumber)) {
      setTempPhoneError('올바른 핸드폰번호 형식을 입력해주세요. (010-0000-0000)');
      return;
    }

    if (userProfile?.phone && tempPhoneNumber !== userProfile.phone) {
      setTempPhoneError('등록된 핸드폰번호와 일치하지 않습니다.');
      return;
    }

    setShowTempAuthModal(false);
    setShowPasswordChangeModal(true);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setConfirmPasswordError('');
  };
  
  const handleCertify = async (certifyData: InisisAuthResult) => {
    try {
      console.log('inicis 인증 성공:', certifyData);
      
      if (!userProfile) {
        toastUtils.error('사용자 정보를 불러올 수 없습니다.');
        return;
      }
      
      const authenticatedName = (certifyData.userName || '').replace(/\s/g, '');
      const currentUserName = (userProfile.name || '').replace(/\s/g, '');
      const authenticatedPhone = formatPhoneNumber(certifyData.userPhone || '');
      const currentUserPhone = userProfile.phone || '';

      const checkDuplicate = await authService.checkDuplicateByDi(certifyData.userDi, certifyData.userCi, certifyData.token);
      if (checkDuplicate) {
        const {isDuplicate, name, phone} = checkDuplicate;
        if (!name || !currentUserName || name !== currentUserName) {
          toastUtils.error('인증된 사용자와 현재 로그인한 사용자가 일치하지 않습니다.');
          return;
        }
  
        if (phone && currentUserPhone && phone !== currentUserPhone) {
          toastUtils.error('인증된 전화번호와 현재 사용자의 전화번호가 일치하지 않습니다.');
          return;
        }
        
        setShowPasswordChangeModal(true);
        setUserCertifyData({userCi: certifyData.userCi, token: certifyData.token});
        setNewPassword('');
        setConfirmPassword('');
        setPasswordError('');
        setConfirmPasswordError('');
      } else {
        toastUtils.error('인증된 사용자와 현재 로그인한 사용자가 일치하지 않습니다.');
        return;
      }

    } catch (error) {
      console.error('인증 처리 중 오류 발생:', error);
      toastUtils.error('인증 처리 중 오류가 발생했습니다.');
    }
  };

  const handlePasswordChangeModalClose = () => {
    setShowPasswordChangeModal(false);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setConfirmPasswordError('');
  };

  const validateConfirmPassword = (password: string, confirmPassword: string): string => {
    if (!confirmPassword) return '비밀번호 확인을 입력해주세요.';
    if (password !== confirmPassword) return '비밀번호가 일치하지 않습니다.';
    return '';
  };

  const handlePasswordChangeSubmit = async () => {
    if (!userCertifyData) {
      toastUtils.error('인증 정보를 불러올 수 없습니다.');
      return;
    }

    const passwordErr = validatePassword(newPassword) || '';
    const confirmPasswordErr = validateConfirmPassword(newPassword, confirmPassword);

    setPasswordError(passwordErr);
    setConfirmPasswordError(confirmPasswordErr);

    if (passwordErr || confirmPasswordErr) {
      return;
    }

    try {
      setIsUpdating(true);
      await accountService.changePassword({ 
        newPassword,
        userCi: userCertifyData.userCi,
        token: userCertifyData.token
      });
      setShowPasswordChangeModal(false);
      setUserCertifyData(undefined);
      toastUtils.success('비밀번호가 성공적으로 변경되었습니다.');
    } catch (error) {
      console.error('비밀번호 변경 실패:', error);
      toastUtils.error('비밀번호 변경에 실패했습니다.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleProfileImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      handleProfileImageUpload([files[0]]);
    }
  };

  useEffect(() => {
    if (profileFiles.length > 0) {
      handleProfileImageSave();
    }
  }, [profileFiles]);

  if (isLoading) {
    return (
      <PageContainer>
        <LoadingState>
          <LoadingSpinnerIcon />
          <LoadingText>계정 정보를 불러오는 중...</LoadingText>
        </LoadingState>
      </PageContainer>
    );
  }

  if (!userProfile) {
    return (
      <PageContainer>
        <ErrorState>
          <AlertCircle size={48} />
          <ErrorTitle>오류가 발생했습니다</ErrorTitle>
          <ErrorText>사용자 정보를 불러올 수 없습니다.</ErrorText>
        </ErrorState>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* 튜토리얼 */}
      {showTutorial && (
        <AccountSettingsTutorial run={showTutorial} onComplete={handleTutorialComplete} />
      )}
      
      <ContentWrapper>
        {/* 히어로 섹션 */}
        <HeroSection data-tutorial="profile-section">
          <HeroBackground>
            <HeroGradient />
            <HeroPattern />
          </HeroBackground>
          <HeroContent>
            <HeroAvatarSection>
              <HeroAvatarWrapper onClick={handleProfileImageClick}>
                <Avatar
                  src={userProfile?.profileImage?.url}
                  alt={userProfile?.name || '사용자'}
                  sizes="xxl"
                />
                <HeroAvatarOverlay>
                  <Camera size={24} />
                </HeroAvatarOverlay>
              </HeroAvatarWrapper>
              <HeroAvatarActions>
                {(userProfile.profileImage || profileFiles.length > 0) && (
                  <HeroAvatarDeleteBtn 
                    onClick={() => setShowProfileDeleteModal(true)}
                    disabled={isUpdating}
                  >
                    <Trash2 size={14} />
                  </HeroAvatarDeleteBtn>
                )}
              </HeroAvatarActions>
            </HeroAvatarSection>
            <HeroTextContent>
              <HeroGreeting>안녕하세요,</HeroGreeting>
              <HeroName>{userProfile.name}</HeroName>
              <HeroLoginId>@{userProfile.loginId}</HeroLoginId>
            </HeroTextContent>
            <HeroStats>
              <HeroStatItem>
                <HeroStatIcon>
                  <Building2 size={18} />
                </HeroStatIcon>
                <HeroStatInfo>
                  <HeroStatValue>{companyInfos.length}</HeroStatValue>
                  <HeroStatLabel>소속 회사</HeroStatLabel>
                </HeroStatInfo>
              </HeroStatItem>
              <HeroStatDivider />
              <HeroStatItem>
                <HeroStatIcon $verified={user?.emailVerified}>
                  {user?.emailVerified ? <Check size={18} /> : <AlertCircle size={18} />}
                </HeroStatIcon>
                <HeroStatInfo>
                  <HeroStatValue>{user?.emailVerified ? '인증됨' : '미인증'}</HeroStatValue>
                  <HeroStatLabel>이메일 상태</HeroStatLabel>
                </HeroStatInfo>
              </HeroStatItem>
            </HeroStats>
          </HeroContent>
        </HeroSection>

        {/* 설정 그리드 */}
        <SettingsGrid data-tutorial="settings-grid">
          {/* 개인 정보 카드 */}
          <SettingsCard $delay={0}>
            <CardHeader>
              <CardIcon $variant="primary">
                <User size={20} />
              </CardIcon>
              <CardHeaderText>
                <CardTitle>개인 정보</CardTitle>
                <CardSubtitle>기본 프로필 정보</CardSubtitle>
              </CardHeaderText>
            </CardHeader>
            
            <CardContent>
              <InfoRow>
                <InfoRowIcon>
                  <UserCircle size={18} />
                </InfoRowIcon>
                <InfoRowContent>
                  <InfoLabel>이름</InfoLabel>
                  <InfoValue>{userProfile.name}</InfoValue>
                </InfoRowContent>
              </InfoRow>
              
              <InfoRow>
                <InfoRowIcon>
                  <Phone size={18} />
                </InfoRowIcon>
                <InfoRowContent>
                  <InfoLabel>전화번호</InfoLabel>
                  <InfoValue>{userProfile.phone || '등록되지 않음'}</InfoValue>
                </InfoRowContent>
              </InfoRow>
            </CardContent>
          </SettingsCard>

          {/* 이메일 카드 */}
          <SettingsCard $delay={0.1}>
            <CardHeader>
              <CardIcon $variant="info">
                <Mail size={20} />
              </CardIcon>
              <CardHeaderText>
                <CardTitle>이메일</CardTitle>
                <CardSubtitle>계정 연락처</CardSubtitle>
              </CardHeaderText>
              <EmailBadge $verified={user?.emailVerified || false}>
                {user?.emailVerified ? (
                  <>
                    <Check size={12} />
                    인증됨
                  </>
                ) : (
                  <>
                    <AlertCircle size={12} />
                    미인증
                  </>
                )}
              </EmailBadge>
            </CardHeader>
            
            <CardContent>
              <EmailValueRow>
                <EmailValue>{userProfile.email}</EmailValue>
                <EmailActions>
                  {user?.emailVerified ? (
                    <ActionBtn onClick={handleEmailEdit} disabled={isUpdating}>
                      <Edit3 size={14} />
                      변경
                    </ActionBtn>
                  ) : (
                    <>
                      <ActionBtn 
                        $variant="primary"
                        onClick={handleResendVerificationEmail} 
                        disabled={isResendingEmail || isUpdating}
                      >
                        <Send size={14} />
                        {isResendingEmail ? '발송중' : '재전송'}
                      </ActionBtn>
                      <ActionBtn onClick={handleEmailEdit} disabled={isUpdating}>
                        <Edit3 size={14} />
                        변경
                      </ActionBtn>
                    </>
                  )}
                </EmailActions>
              </EmailValueRow>
              
              {!user?.emailVerified && (
                <EmailWarningBox>
                  <AlertCircle size={16} />
                  <span>이메일 인증을 완료해야 모든 서비스를 이용할 수 있습니다.</span>
                </EmailWarningBox>
              )}
            </CardContent>
          </SettingsCard>

          {/* 계정 보안 카드 */}
          <SettingsCard $delay={0.2} $fullWidth>
            <CardHeader>
              <CardIcon $variant="warning">
                <Shield size={20} />
              </CardIcon>
              <CardHeaderText>
                <CardTitle>계정 보안</CardTitle>
                <CardSubtitle>로그인 정보 및 비밀번호</CardSubtitle>
              </CardHeaderText>
            </CardHeader>
            
            <CardContent>
              <SecurityGrid>
                <SecurityItem>
                  <SecurityItemIcon>
                    <User size={20} />
                  </SecurityItemIcon>
                  <SecurityItemContent>
                    <SecurityItemLabel>아이디</SecurityItemLabel>
                    <SecurityItemValue>{userProfile.loginId}</SecurityItemValue>
                    <SecurityItemDesc>로그인에 사용되는 고유 아이디</SecurityItemDesc>
                  </SecurityItemContent>
                </SecurityItem>
                
                <SecurityDivider />
                
                <SecurityItem>
                  <SecurityItemIcon>
                    <Lock size={20} />
                  </SecurityItemIcon>
                  <SecurityItemContent>
                    <SecurityItemLabel>비밀번호</SecurityItemLabel>
                    <SecurityItemValue>••••••••</SecurityItemValue>
                    <SecurityItemDesc>계정 보안을 위해 정기적으로 변경해주세요</SecurityItemDesc>
                  </SecurityItemContent>
                  <SecurityItemAction>
                    {config.environment === 'production' ? (
                    <InisisAuthButton 
                      $variant='outline'
                      $size='sm'
                      onSuccess={handleCertify}
                      $title='변경하기'
                    />
                    ) : (
                      <ChangePasswordBtn onClick={handleTempAuthModalOpen} disabled={isUpdating}>
                        <KeyRound size={16} />
                        변경하기
                      </ChangePasswordBtn>
                    )}
                  </SecurityItemAction>
                </SecurityItem>
              </SecurityGrid>
            </CardContent>
          </SettingsCard>
        </SettingsGrid>

        {/* 알림 관리 섹션 */}
        <NotificationSection>
          <SectionHeader>
            <SectionIcon style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(96, 165, 250, 0.2) 100%)', color: '#3b82f6' }}>
              <Bell size={24} />
            </SectionIcon>
            <SectionHeaderText>
              <SectionTitle>알림 관리</SectionTitle>
              <SectionSubtitle>알림톡·문자 수신 여부를 설정하세요</SectionSubtitle>
            </SectionHeaderText>
          </SectionHeader>

          <NotificationCard>
            <NotificationRow>
              <NotificationText>
                <NotificationLabel>알림톡 수신</NotificationLabel>
                <NotificationDesc>
                  카카오 알림톡 및 문자(SMS) 알림을 받습니다. 이메일·앱 내 알림은 영향을 받지 않습니다.
                </NotificationDesc>
              </NotificationText>
              <ToggleSwitch
                checked={alimtalkSmsEnabled}
                onChange={handleAlimtalkPreferenceChange}
                disabled={isUpdatingPreference}
                size="medium"
              />
            </NotificationRow>
          </NotificationCard>
        </NotificationSection>

        {/* 회사 소속 섹션 */}
        {companyInfos.length > 0 && (
          <CompanySection>
            <SectionHeader>
              <SectionIcon>
                <Building2 size={24} />
              </SectionIcon>
              <SectionHeaderText>
                <SectionTitle>소속 회사</SectionTitle>
                <SectionSubtitle>회사별 계정 정보를 확인하세요</SectionSubtitle>
              </SectionHeaderText>
              <SectionBadge>{companyInfos.length}개</SectionBadge>
            </SectionHeader>
            
            <CompanyGrid>
              {companyInfos.map((company, index) => (
                <CompanyCard key={index} $delay={index * 0.1}>
                  <CompanyCardHeader>
                    <CompanyAvatar>
                      <Building2 size={24} />
                    </CompanyAvatar>
                    <CompanyHeaderText>
                      <CompanyName>{company.companyName}</CompanyName>
                      <CompanyEmail>{company.email}</CompanyEmail>
                    </CompanyHeaderText>
                  </CompanyCardHeader>
                  
                  <CompanyCardContent>
                    <CompanyInfoGrid>
                      <CompanyInfoItem>
                        <CompanyInfoIcon>
                          <UserCircle size={16} />
                        </CompanyInfoIcon>
                        <CompanyInfoText>
                          <CompanyInfoLabel>닉네임</CompanyInfoLabel>
                          <CompanyInfoValue>{company.nickname || '-'}</CompanyInfoValue>
                        </CompanyInfoText>
                      </CompanyInfoItem>
                      <CompanyInfoItem>
                        <CompanyInfoIcon>
                          <Briefcase size={16} />
                        </CompanyInfoIcon>
                        <CompanyInfoText>
                          <CompanyInfoLabel>직책</CompanyInfoLabel>
                          <CompanyInfoValue>{company.position || '-'}</CompanyInfoValue>
                        </CompanyInfoText>
                      </CompanyInfoItem>
                    </CompanyInfoGrid>
                    
                    {company.departments && company.departments.length > 0 && (
                      <DepartmentSection>
                        <DepartmentHeader>
                          <Users size={14} />
                          소속 조직
                        </DepartmentHeader>
                        <DepartmentList>
                          {company.departments.map((dept, deptIndex) => (
                            <DepartmentTag key={deptIndex}>{dept.name}</DepartmentTag>
                          ))}
                        </DepartmentList>
                      </DepartmentSection>
                    )}
                  </CompanyCardContent>
                </CompanyCard>
              ))}
            </CompanyGrid>
          </CompanySection>
        )}
        
        {/* 튜토리얼 다시 보기 섹션 */}
        <TutorialSection>
          <SectionHeader>
            <SectionIcon style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
              <GraduationCap size={24} color="#fff" />
            </SectionIcon>
            <SectionHeaderText>
              <SectionTitle>튜토리얼</SectionTitle>
              <SectionSubtitle>팩트시트 사용법을 다시 확인하세요</SectionSubtitle>
            </SectionHeaderText>
          </SectionHeader>
          
          <TutorialGrid>
            <TutorialCard onClick={() => handleRestartTutorial('my-space')}>
              <TutorialCardIcon $color="#6366f1">
                <HiOutlineHome size={24} />
              </TutorialCardIcon>
              <TutorialCardContent>
                <TutorialCardTitle>마이스페이스</TutorialCardTitle>
                <TutorialCardDesc>개인 대시보드 사용법</TutorialCardDesc>
              </TutorialCardContent>
              <PlayCircle size={20} color="#6366f1" />
            </TutorialCard>
            
            <TutorialCard onClick={() => handleRestartTutorial('space-management')}>
              <TutorialCardIcon $color="#059669">
                <HiOutlineBuildingOffice2 size={24} />
              </TutorialCardIcon>
              <TutorialCardContent>
                <TutorialCardTitle>회사 관리</TutorialCardTitle>
                <TutorialCardDesc>회사 및 스페이스 관리법</TutorialCardDesc>
              </TutorialCardContent>
              <PlayCircle size={20} color="#059669" />
            </TutorialCard>
            
            <TutorialCard onClick={() => handleRestartTutorial('account-settings')}>
              <TutorialCardIcon $color="#8b5cf6">
                <HiOutlineUserCircle size={24} />
              </TutorialCardIcon>
              <TutorialCardContent>
                <TutorialCardTitle>계정 설정</TutorialCardTitle>
                <TutorialCardDesc>프로필 및 보안 관리법</TutorialCardDesc>
              </TutorialCardContent>
              <PlayCircle size={20} color="#8b5cf6" />
            </TutorialCard>
          </TutorialGrid>
          
          <TutorialAllBtn onClick={handleRestartAllTutorials}>
            <RotateCcw size={18} />
            전체 튜토리얼 다시 보기
          </TutorialAllBtn>
        </TutorialSection>
        
        {/* 숨겨진 파일 입력 */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileInputChange}
          accept="image/*"
          style={{ display: 'none' }}
        />
      </ContentWrapper>
      
      {/* 로딩 오버레이 */}
      {isUpdating && (
        <LoadingOverlay>
          <LoadingModal>
            <LoadingSpinnerIcon />
            <LoadingModalText>처리 중...</LoadingModalText>
          </LoadingModal>
        </LoadingOverlay>
      )}

      {/* 모달들 */}
      {showTempAuthModal && (
        <TempAuthModal
          isOpen={showTempAuthModal}
          onClose={handleTempAuthModalClose}
          tempPhoneNumber={tempPhoneNumber}
          tempPhoneError={tempPhoneError}
          isUpdating={isUpdating}
          onPhoneNumberChange={handleTempPhoneNumberChange}
          onSubmit={handleTempAuthSubmit}
        />
      )}

      {showEmailChangeModal && (
        <EmailChangeModal
          isOpen={showEmailChangeModal}
          onClose={handleEmailCancel}
          currentEmail={userProfile?.email || ''}
          onSave={handleEmailSave}
        />
      )}

      {showProfileDeleteModal && (
        <ProfileDeleteModal
          isOpen={showProfileDeleteModal}
          onClose={() => setShowProfileDeleteModal(false)}
          onDelete={handleProfileImageRemove}
          isUpdating={isUpdating}
        />
      )}

      {showPasswordChangeModal && (
        <PasswordChangeModal
          isOpen={showPasswordChangeModal}
          onClose={handlePasswordChangeModalClose}
          newPassword={newPassword}
          confirmPassword={confirmPassword}
          passwordError={passwordError}
          confirmPasswordError={confirmPasswordError}
          isUpdating={isUpdating}
          onNewPasswordChange={setNewPassword}
          onConfirmPasswordChange={setConfirmPassword}
          onPasswordBlur={() => setPasswordError(validatePassword(newPassword) || '')}
          onConfirmPasswordBlur={() => setConfirmPasswordError(validateConfirmPassword(newPassword, confirmPassword))}
          onSubmit={handlePasswordChangeSubmit}
        />
      )}
    </PageContainer>
  );
};

// ============================================
// Animations
// ============================================

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// ============================================
// Styled Components
// ============================================

const PageContainer = styled.div`
  min-height: 100%;
  height: 100%;
  padding: 1.5rem;
  background: ${semanticColors.background.primary};
  overflow-y: auto;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
`;

// 히어로 섹션
const HeroSection = styled.div`
  position: relative;
  margin-bottom: 2rem;
  border-radius: 24px;
  overflow: hidden;
  animation: ${fadeInUp} 0.6s ease-out;
`;

const HeroBackground = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a78bfa 100%);
`;

const HeroGradient = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 30% 20%, rgba(255,255,255,0.15) 0%, transparent 50%),
              radial-gradient(circle at 70% 80%, rgba(255,255,255,0.1) 0%, transparent 40%);
`;

const HeroPattern = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.1;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
`;

const HeroContent = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 2rem;
  padding: 2.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    padding: 2rem;
  }
`;

const HeroAvatarSection = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const HeroAvatarWrapper = styled.div`
  position: relative;
  cursor: pointer;
  border-radius: 50%;
  overflow: hidden;
  border: 4px solid rgba(255,255,255,0.3);
  transition: all 0.3s ease;
  
  &:hover {
    border-color: rgba(255,255,255,0.6);
    transform: scale(1.02);
  }
`;

const HeroAvatarOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  opacity: 0;
  transition: opacity 0.3s ease;
  
  ${HeroAvatarWrapper}:hover & {
    opacity: 1;
  }
`;

const HeroAvatarActions = styled.div`
  position: absolute;
  bottom: -4px;
  right: -4px;
`;

const HeroAvatarDeleteBtn = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${semanticColors.background.danger.primary};
  border: 3px solid #6366f1;
  border-radius: 50%;
  color: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    transform: scale(1.1);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const HeroTextContent = styled.div`
  flex: 1;
`;

const HeroGreeting = styled.span`
  ${typography.caption.caption1.default}
  color: rgba(255,255,255,0.95);
  display: block;
  margin-bottom: 0.25rem;
`;

const HeroName = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 0.25rem;
`;

const HeroLoginId = styled.span`
  ${typography.paragraph.p2.default}
  color: rgba(255,255,255,0.85);
  font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const HeroStats = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1rem 1.5rem;
  background: rgba(255,255,255,0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 16px;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const HeroStatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const HeroStatIcon = styled.div<{ $verified?: boolean }>`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  color: #fff;
  background: ${props => props.$verified === false 
    ? 'rgba(239, 68, 68, 0.3)' 
    : 'rgba(255, 255, 255, 0.2)'};
`;

const HeroStatInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const HeroStatValue = styled.span`
  font-size: 1.25rem;
  font-weight: 700;
  color: #fff;
  line-height: 1.2;
`;

const HeroStatLabel = styled.span`
  ${typography.caption.caption2}
  color: rgba(255,255,255,0.9);
`;

const HeroStatDivider = styled.div`
  width: 1px;
  height: 40px;
  background: rgba(255,255,255,0.2);
`;

// 설정 그리드
const SettingsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.25rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SettingsCard = styled.div<{ $delay: number; $fullWidth?: boolean }>`
  background: ${semanticColors.background.secondary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 20px;
  overflow: hidden;
  animation: ${fadeInUp} 0.5s ease-out ${props => props.$delay}s both;
  
  ${props => props.$fullWidth && `
    grid-column: 1 / -1;
  `}
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid ${semanticColors.border.primary};
`;

const CardIcon = styled.div<{ $variant: 'primary' | 'info' | 'warning' | 'success' }>`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  flex-shrink: 0;
  
  ${props => props.$variant === 'primary' && `
    background: linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.2) 100%);
    color: #6366f1;
  `}
  
  ${props => props.$variant === 'info' && `
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(96, 165, 250, 0.2) 100%);
    color: #3b82f6;
  `}
  
  ${props => props.$variant === 'warning' && `
    background: linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(251, 191, 36, 0.2) 100%);
    color: #f59e0b;
  `}
  
  ${props => props.$variant === 'success' && `
    background: linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(52, 211, 153, 0.2) 100%);
    color: #10b981;
  `}
`;

const CardHeaderText = styled.div`
  flex: 1;
`;

const CardTitle = styled.h3`
  ${typography.subtitle.subtitle1}
  color: ${semanticColors.text.primary};
  margin-bottom: 0.125rem;
`;

const CardSubtitle = styled.p`
  ${typography.caption.caption2}
  color: ${semanticColors.text.tertiary};
`;

const CardContent = styled.div`
  padding: 1.5rem;
`;

// 정보 행
const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.875rem 0;
  border-bottom: 1px solid ${semanticColors.border.tertiary};
  
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
  
  &:first-child {
    padding-top: 0;
  }
`;

const InfoRowIcon = styled.div`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${semanticColors.background.tertiary};
  border-radius: 8px;
  color: ${semanticColors.text.tertiary};
  flex-shrink: 0;
`;

const InfoRowContent = styled.div`
  flex: 1;
`;

const InfoLabel = styled.div`
  ${typography.caption.caption2}
  color: ${semanticColors.text.tertiary};
  margin-bottom: 0.125rem;
`;

const InfoValue = styled.div`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.primary};
  font-weight: 500;
`;

// 이메일 섹션
const EmailBadge = styled.span<{ $verified: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.75rem;
  border-radius: 100px;
  ${typography.caption.caption2}
  font-weight: 600;
  
  ${props => props.$verified ? `
    background: ${semanticColors.background.success.secondary};
    color: ${semanticColors.text.onSuccess.primary};
  ` : `
    background: ${semanticColors.background.danger.secondary};
    color: ${semanticColors.text.onDanger.secondary};
  `}
`;

const EmailValueRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const EmailValue = styled.span`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.primary};
  font-weight: 500;
`;

const EmailActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionBtn = styled.button<{ $variant?: 'primary' }>`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.875rem;
  border-radius: 8px;
  ${typography.caption.caption1.medium}
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.$variant === 'primary' ? `
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    border: none;
    color: #fff;
    
    &:hover:not(:disabled) {
      box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
      transform: translateY(-1px);
    }
  ` : `
    background: ${semanticColors.background.secondary};
    border: 1px solid ${semanticColors.border.primary};
    color: ${semanticColors.text.secondary};
    
    &:hover:not(:disabled) {
      background: ${semanticColors.background.primaryHover};
      border-color: ${semanticColors.border.secondary};
    }
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const EmailWarningBox = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1rem;
  padding: 0.875rem 1rem;
  background: ${semanticColors.background.danger.secondary};
  border: 1px solid ${semanticColors.border.dangerPrimary};
  border-radius: 10px;
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.onDanger.secondary};
`;

// 보안 섹션
const SecurityGrid = styled.div`
  display: flex;
  flex-direction: column;
`;

const SecurityItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.25rem 0;
  
  &:first-child {
    padding-top: 0;
  }
  
  &:last-child {
    padding-bottom: 0;
  }
`;

const SecurityItemIcon = styled.div`
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${semanticColors.background.tertiary};
  border-radius: 12px;
  color: ${semanticColors.text.secondary};
  flex-shrink: 0;
`;

const SecurityItemContent = styled.div`
  flex: 1;
`;

const SecurityItemLabel = styled.div`
  ${typography.subtitle.subtitle2}
  color: ${semanticColors.text.primary};
  margin-bottom: 0.25rem;
`;

const SecurityItemValue = styled.div`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.secondary};
  margin-bottom: 0.25rem;
`;

const SecurityItemDesc = styled.div`
  ${typography.caption.caption2}
  color: ${semanticColors.text.tertiary};
`;

const SecurityItemAction = styled.div`
  flex-shrink: 0;
`;

const SecurityDivider = styled.div`
  height: 1px;
  background: ${semanticColors.border.primary};
  margin: 0;
`;

const ChangePasswordBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);
  border: none;
  border-radius: 10px;
  ${typography.paragraph.p2.default}
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

// 알림 관리 섹션
const NotificationSection = styled.section`
  margin-top: 2rem;
  animation: ${fadeInUp} 0.6s ease-out 0.25s both;
`;

const NotificationCard = styled.div`
  background: ${semanticColors.background.secondary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 16px;
  padding: 1.25rem 1.5rem;
`;

const NotificationRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
`;

const NotificationText = styled.div`
  flex: 1;
  min-width: 0;
`;

const NotificationLabel = styled.p`
  ${typography.subtitle.subtitle2}
  color: ${semanticColors.text.primary};
`;

const NotificationDesc = styled.p`
  ${typography.caption.caption2}
  color: ${semanticColors.text.tertiary};
  margin-top: 0.25rem;
`;

// 회사 섹션
const CompanySection = styled.section`
  margin-top: 2rem;
  animation: ${fadeInUp} 0.6s ease-out 0.3s both;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
`;

const SectionIcon = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(52, 211, 153, 0.2) 100%);
  border-radius: 14px;
  color: #10b981;
`;

const SectionHeaderText = styled.div`
  flex: 1;
`;

const SectionTitle = styled.h2`
  ${typography.subtitle.subtitle1}
  color: ${semanticColors.text.primary};
`;

const SectionSubtitle = styled.p`
  ${typography.caption.caption2}
  color: ${semanticColors.text.tertiary};
  margin-top: 0.125rem;
`;

const SectionBadge = styled.span`
  padding: 0.375rem 0.875rem;
  background: ${semanticColors.background.success.secondary};
  border-radius: 100px;
  ${typography.caption.caption1.medium}
  color: ${semanticColors.text.onSuccess.primary};
`;

const CompanyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 1.25rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CompanyCard = styled.div<{ $delay: number }>`
  background: ${semanticColors.background.secondary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.25s ease;
  animation: ${fadeIn} 0.4s ease-out ${props => props.$delay}s both;
  
  &:hover {
    border-color: #10b981;
    box-shadow: 0 8px 24px rgba(16, 185, 129, 0.1);
    transform: translateY(-2px);
  }
`;

const CompanyCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 1.25rem;
  border-bottom: 1px solid ${semanticColors.border.primary};
`;

const CompanyAvatar = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #10b981 0%, #34d399 100%);
  border-radius: 12px;
  color: #fff;
  flex-shrink: 0;
`;

const CompanyHeaderText = styled.div`
  flex: 1;
  min-width: 0;
`;

const CompanyName = styled.h3`
  ${typography.subtitle.subtitle2}
  color: ${semanticColors.text.primary};
  margin-bottom: 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CompanyEmail = styled.p`
  ${typography.caption.caption1.default}
  color: #10b981;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CompanyCardContent = styled.div`
  padding: 1.25rem;
`;

const CompanyInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
`;

const CompanyInfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
`;

const CompanyInfoIcon = styled.div`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${semanticColors.background.tertiary};
  border-radius: 6px;
  color: ${semanticColors.text.tertiary};
  flex-shrink: 0;
`;

const CompanyInfoText = styled.div`
  flex: 1;
`;

const CompanyInfoLabel = styled.div`
  ${typography.caption.caption2}
  color: ${semanticColors.text.tertiary};
  margin-bottom: 0.125rem;
`;

const CompanyInfoValue = styled.div`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.primary};
  font-weight: 500;
`;

const DepartmentSection = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid ${semanticColors.border.tertiary};
`;

const DepartmentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  ${typography.caption.caption1.medium}
  color: ${semanticColors.text.tertiary};
  margin-bottom: 0.625rem;
`;

const DepartmentList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const DepartmentTag = styled.span`
  padding: 0.375rem 0.75rem;
  background: ${semanticColors.background.tertiary};
  border-radius: 100px;
  ${typography.caption.caption2}
  color: ${semanticColors.text.secondary};
  font-weight: 500;
`;

// 로딩 및 에러 상태
const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  gap: 1rem;
`;

const LoadingSpinnerIcon = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid ${semanticColors.border.primary};
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const LoadingText = styled.p`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.tertiary};
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  gap: 1rem;
  color: ${semanticColors.text.onDanger.secondary};
`;

const ErrorTitle = styled.h3`
  ${typography.subtitle.subtitle1}
  color: ${semanticColors.text.primary};
`;

const ErrorText = styled.p`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.tertiary};
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${fadeIn} 0.2s ease-out;
`;

const LoadingModal = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem;
  background: ${semanticColors.background.secondary};
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
`;

const LoadingModalText = styled.p`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.secondary};
`;

// 튜토리얼 섹션
const TutorialSection = styled.div`
  margin-top: 2rem;
  animation: ${fadeInUp} 0.6s ease-out 0.4s both;
`;

const TutorialGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-top: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TutorialCard = styled.button`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  background: ${semanticColors.background.secondary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  
  &:hover {
    border-color: ${semanticColors.border.brand};
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
    transform: translateY(-2px);
  }
`;

const TutorialCardIcon = styled.div<{ $color: string }>`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.$color}15;
  border-radius: 12px;
  color: ${props => props.$color};
  flex-shrink: 0;
`;

const TutorialCardContent = styled.div`
  flex: 1;
`;

const TutorialCardTitle = styled.h4`
  ${typography.subtitle.subtitle2}
  color: ${semanticColors.text.primary};
  margin-bottom: 0.25rem;
`;

const TutorialCardDesc = styled.p`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.tertiary};
`;

const TutorialAllBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 1rem;
  margin-top: 1rem;
  background: ${semanticColors.background.tertiary};
  border: 1px dashed ${semanticColors.border.secondary};
  border-radius: 12px;
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${semanticColors.background.brand};
    border-color: ${semanticColors.border.brand};
    color: ${semanticColors.text.brand};
  }
`;

export default AccountSettingsPage; 
